import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { useFormContext, useWatch } from 'react-hook-form';
import { useQuery } from 'urql';
import FormSaveButton from '@/components/Custom/FormSaveButton';
import ModalKeyboardStickyView from '@/components/Custom/ModalKeyboardStickyView';
import type { EventGenerateFormValues } from '@/components/Event/EventGenerate.types';
import {
  createEventGenerateInitialGames,
  getEventGenerateScheduleSlotCount,
} from '@/components/Event/EventGenerate.utils';
import EventGenerateHeader from '@/components/Event/EventGenerateHeader';
import TeamSelect from '@/components/Team/TeamSelect';
import type { TeamSelectItemArgs } from '@/components/Team/TeamSelect.types';
import Box from '@/components/UI/Box';
import Card from '@/components/UI/Card';
import Text from '@/components/UI/Text';
import { graphql } from '@/gql/graphql';

const GetEventGenerateTeamSource = graphql(/* GraphQL */ `
  query GetEventGenerateTeamSource($eventId: uuid!) {
    event_by_pk(id: $eventId) {
      id
      rootParentId: root_parent_id
    }
  }
`);

export default function EventGenerate() {
  const { eventId } = useLocalSearchParams<{
    eventId: string;
  }>();
  const { push } = useRouter();
  const { setValue, getValues, control } =
    useFormContext<EventGenerateFormValues>();
  const selectedTeams = useWatch({
    control,
    name: 'selectedTeams',
    defaultValue: [],
  });
  const [{ data: teamSourceData }] = useQuery({
    query: GetEventGenerateTeamSource,
    variables: { eventId },
  });
  const canContinue = selectedTeams.length > 1;
  const rootParentId = teamSourceData?.event_by_pk?.rootParentId;
  const teamsWhere = {
    _or: [
      {
        team_events: {
          event_id: {
            _eq: eventId,
          },
        },
      },
      ...(rootParentId
        ? [
            {
              root_event_id: {
                _eq: rootParentId,
              },
            },
          ]
        : []),
    ],
  };
  const isChecked = (id: string) =>
    selectedTeams?.some((team) => team.id === id) ?? false;

  const onPressTeam = (team: TeamSelectItemArgs) => {
    const currentSelectedTeams = getValues('selectedTeams') ?? [];

    if (
      currentSelectedTeams.some((selectedTeam) => selectedTeam.id === team.id)
    ) {
      setValue(
        'selectedTeams',
        currentSelectedTeams.filter(
          (selectedTeam) => selectedTeam.id !== team.id,
        ),
        {
          shouldDirty: true,
          shouldValidate: true,
        },
      );
      setValue('games', [], {
        shouldDirty: true,
        shouldValidate: true,
      });
      return;
    }

    setValue(
      'selectedTeams',
      [
        ...currentSelectedTeams,
        {
          id: team.id,
          title: team.title,
          home_primary_color: team.home_primary_color,
          avatar_id: team.avatar_id,
        },
      ],
      {
        shouldDirty: true,
        shouldValidate: true,
      },
    );
    setValue('games', [], {
      shouldDirty: true,
      shouldValidate: true,
    });
  };
  const onPressNext = () => {
    const selectedTeams = getValues('selectedTeams');
    const initialGames = getValues('initialGames') ?? [];
    const expectedSlotCount = getEventGenerateScheduleSlotCount(
      selectedTeams.length,
    );

    if (selectedTeams.length < 2) {
      return;
    }

    if (initialGames.length !== expectedSlotCount) {
      setValue(
        'initialGames',
        createEventGenerateInitialGames(selectedTeams.length),
        {
          shouldDirty: true,
          shouldValidate: true,
        },
      );
    }

    setValue('games', [], {
      shouldDirty: true,
      shouldValidate: true,
    });
    push({
      pathname: '/event/[eventId]/generate/slots',
      params: {
        eventId,
      },
    });
  };
  return (
    <>
      <Stack.Screen options={{}} />

      <Stack.Screen
        options={{
          title: 'Generate Schedule',
        }}
      />

      <Box direction="vertical" space="md" px="xl">
        <EventGenerateHeader />
        <Text center size="3xl" variant="primary">
          STEP 1/4
        </Text>
        <Text center size="2xl">
          Let's generate your schedule.
        </Text>
        <Text center size="lg">
          First, choose the teams you want to create games for.
        </Text>
      </Box>

      <Box flex px="xl" py="md">
        <Card flex={1}>
          <TeamSelect
            extraData={selectedTeams}
            isChecked={isChecked}
            onPressItem={onPressTeam}
            where={teamsWhere}
            search=""
          />
        </Card>
      </Box>

      <ModalKeyboardStickyView>
        <FormSaveButton
          isDisabled={!canContinue}
          info={{ text: 'Next' }}
          onPress={onPressNext}
        />
      </ModalKeyboardStickyView>
    </>
  );
}
